import { defineStore } from 'pinia'
import streamerApi from '../api/streamerApi'
import usersApi from '../api/usersApi'
import { PlayerMouseWheelScrollSensitivity } from '../enums/playerMouseWheelScrollSensitivity'
import { PlayerResolution } from '../enums/playerResolution'
import type { User } from '../interfaces/User'
import { useCursorStore } from './cursor'
import { useInactivityTimerStore } from './inactivityTimer'
import { useMachineStore } from './machine'
import { useStore } from './main'
import { useResolutionStore } from './resolution'

interface State {
    _user: User | null
    _persistedCursorCaptureMode: boolean
    keepSessionAlivePollInterval: number | null
}

export const useUserStore = defineStore('user', {
    state: (): State => ({
        _user: null,
        _persistedCursorCaptureMode: false,
        keepSessionAlivePollInterval: null,
    }),

    getters: {
        user: (state: State): User => {
            if (!state._user) {
                throw new Error('User has not been initialized.')
            }

            return state._user
        },

        persistedCursorCaptureMode: (state: State): boolean =>
            state._persistedCursorCaptureMode,
    },

    actions: {
        init(user: User): void {
            const machineStore = useMachineStore()

            this._user = user
            this._persistedCursorCaptureMode =
                user.player_preferences.is_cursor_captured_mode_enabled

            // Forbid upscale mode for Android device
            if (machineStore.isRunningOnAndroid) {
                this.user.player_preferences.is_upscale_mode_enabled = false
            }
        },

        isUserInitialized(): boolean {
            return !!this._user
        },

        startKeepSessionAlivePoll(): void {
            if (this.keepSessionAlivePollInterval) {
                return
            }

            this.keepSessionAlivePollInterval = window.setInterval(
                () => {
                    usersApi
                        .keepSessionAlive()
                        .catch((error: Error) => console.error(error))
                },
                10 * 60 * 1000 // Each 10 minutes
            )
        },

        async savePlayerMouseWheelScrollSensitivity(
            playerMouseWheelScrollSensitivity: PlayerMouseWheelScrollSensitivity
        ): Promise<void> {
            const mainStore = useStore()
            const cursorStore = useCursorStore()

            if (
                mainStore.readonly
                || mainStore.mobile
                || cursorStore.isTouchModeEnabled
            ) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    mousewheel_scroll_sensitivity:
                        playerMouseWheelScrollSensitivity,
                })

                this.user.player_preferences.mousewheel_scroll_sensitivity =
                    response.data.mousewheel_scroll_sensitivity
            } catch (error) {
                console.error(
                    'Failed to update Player mouse wheel scroll sensitivity preference:',
                    error
                )
            }
        },

        async savePlayerResolutionPreference(
            resolution: PlayerResolution
        ): Promise<void> {
            const mainStore = useStore()
            const resolutionStore = useResolutionStore()

            if (mainStore.readonly) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    resolution: resolution,
                })

                this.user.player_preferences.resolution =
                    response.data.resolution

                resolutionStore.updateRemoteResolution()
            } catch (error) {
                console.error(
                    'Failed to update Player resolution preference:',
                    error
                )
            }
        },

        async savePlayerStreamBitratePreference(
            streamBitrateInMegabytesPerSecond: number
        ): Promise<void> {
            const mainStore = useStore()

            if (mainStore.readonly) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    stream_bitrate_mbps: streamBitrateInMegabytesPerSecond,
                })

                this.user.player_preferences.stream_bitrate_mbps =
                    response.data.stream_bitrate_mbps

                streamerApi.updateBitrate(response.data.stream_bitrate_mbps)
            } catch (error) {
                console.error(
                    'Failed to update Player stream bitrate preference:',
                    error
                )
            }
        },

        async saveFullscreenModePlayerPreference(
            isPlayerFullScreenModeEnabled: boolean
        ): Promise<void> {
            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_fullscreen_mode_enabled: isPlayerFullScreenModeEnabled,
                })

                this.user.player_preferences.is_fullscreen_mode_enabled =
                    response.data.is_fullscreen_mode_enabled
            } catch (error) {
                console.error(
                    'Failed to update Player fullscreen mode preference:',
                    error
                )
            }
        },

        async togglePlayerUpscaleModePreference(): Promise<void> {
            const machineStore = useMachineStore()

            // Forbid to change upscale mode for Android devices
            if (machineStore.isRunningOnAndroid) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_upscale_mode_enabled:
                        !this.user.player_preferences.is_upscale_mode_enabled,
                })

                this.user.player_preferences.is_upscale_mode_enabled =
                    response.data.is_upscale_mode_enabled
            } catch (error) {
                console.error(
                    'Failed to update Player Upscale mode preference:',
                    error
                )
            }
        },

        async togglePlayerFpsOptimizationPreference(): Promise<void> {
            const mainStore = useStore()

            if (mainStore.readonly) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_fps_optimization_enabled:
                        !this.user.player_preferences
                            .is_fps_optimization_enabled,
                })

                streamerApi.updateFpsOptimizationActivation(
                    response.data.is_fps_optimization_enabled
                )

                this.user.player_preferences.is_fps_optimization_enabled =
                    response.data.is_fps_optimization_enabled
            } catch (error) {
                console.error(
                    'Failed to update Player FPS optimization preference:',
                    error
                )
            }
        },

        async toggleMetricsPanelDisplayPreference(): Promise<void> {
            try {
                const currentValue =
                    this.user.player_preferences.is_metrics_panel_displayed
                const newValue = !currentValue

                const response = await usersApi.patchPlayerPreferences({
                    is_metrics_panel_displayed: newValue,
                })

                this.user.player_preferences.is_metrics_panel_displayed =
                    response.data.is_metrics_panel_displayed
            } catch (error) {
                console.error(
                    'Failed to update metrics panel display preference:',
                    error
                )
            }
        },

        async toggleMetricsPanelFoldedPreference(): Promise<void> {
            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_metrics_panel_folded:
                        !this.user.player_preferences.is_metrics_panel_folded,
                })

                this.user.player_preferences.is_metrics_panel_folded =
                    response.data.is_metrics_panel_folded
            } catch (error) {
                console.error(
                    'Failed to update metrics panel folded preference:',
                    error
                )
            }
        },

        async toggleAutoDisconnectPlayerPreference(): Promise<void> {
            const mainStore = useStore()

            if (mainStore.readonly) {
                return
            }

            const inactivityTimerStore = useInactivityTimerStore()

            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_auto_disconnect_enabled:
                        !this.user.player_preferences
                            .is_auto_disconnect_enabled,
                })

                this.user.player_preferences.is_auto_disconnect_enabled =
                    response.data.is_auto_disconnect_enabled

                response.data.is_auto_disconnect_enabled
                    ? inactivityTimerStore.startTimers()
                    : inactivityTimerStore.stopTimers()
            } catch (error) {
                console.error(
                    'Failed to update auto disconnect preference:',
                    error
                )
            }
        },

        async toggleSideMenuBurgerButtonDisplayPreference(): Promise<void> {
            const mainStore = useStore()

            if (mainStore.readonly) {
                return
            }

            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_side_menu_burger_button_displayed:
                        !this.user.player_preferences
                            .is_side_menu_burger_button_displayed,
                })

                this.user.player_preferences.is_side_menu_burger_button_displayed =
                    response.data.is_side_menu_burger_button_displayed
            } catch (error) {
                console.error(
                    'Failed to update side menu burger button display preference:',
                    error
                )
            }
        },

        async saveCursorCaptureModePreference(
            isCursorCaptureModeEnabled: boolean
        ): Promise<void> {
            try {
                const response = await usersApi.patchPlayerPreferences({
                    is_cursor_captured_mode_enabled: isCursorCaptureModeEnabled,
                })

                this._persistedCursorCaptureMode =
                    response.data.is_cursor_captured_mode_enabled
            } catch (error) {
                console.error(
                    'Failed to update cursor capture mode preference:',
                    error
                )
            }
        },

        setCursorCaptureModeState(isCursorCaptureModeEnabled: boolean): void {
            this.user.player_preferences.is_cursor_captured_mode_enabled =
                isCursorCaptureModeEnabled
        },

        async saveSideMenuShortcutPreference(
            shortcut: string[]
        ): Promise<void> {
            try {
                const response = await usersApi.patchPlayerPreferences({
                    side_menu_shortcut: shortcut,
                })

                this.user.player_preferences.side_menu_shortcut =
                    response.data.side_menu_shortcut
            } catch {
                // Toast already displayed by http utility
            }
        },
    },
})
